/**
 * Every rule this firm has written, and which of them will actually fire.
 *
 * Rules were writable and unreadable: a pack could hold one that could never
 * match anything, and the only way to find out was a figure that looked wrong.
 * Shadowed rules are called out here instead.
 */

import { useState } from 'react';
import { useModule } from '../modules/useModule';
import { useToast } from '../../provider/toast';
import {
	useCanonicalFieldsQuery,
	useDeleteRuleMutation,
	usePublishRulesetMutation,
	useRulesetsQuery,
} from '../../queries/firm';
import {
	Card,
	ConfirmDialog,
	Dialog,
	ErrorNotice,
	Note,
	StatusBadge,
} from '../../shared/ui';
import RuleBuilder from './RuleBuilder';
import type {
	Classification,
	FirmRule as Rule,
	RulesetPack as Ruleset,
} from '../../schemas';
import { Button } from '@mui/material';

const BLANK_OPERATORS = new Set(['IS_BLANK', 'IS_NOT_BLANK']);

/* One phrase per operator, used only by `sentence` now that editing a
   condition happens entirely inside `RuleBuilder` (which keeps its own copy
   of this table for its dropdowns — see its header comment on why the
   server's wording is the one that counts if they ever disagree). */
const OPERATOR_LABELS: Record<string, string> = {
	EQUALS: 'is',
	NOT_EQUALS: 'is not',
	CONTAINS: 'contains',
	NOT_CONTAINS: 'does not contain',
	STARTS_WITH: 'starts with',
	NOT_STARTS_WITH: 'does not start with',
	ENDS_WITH: 'ends with',
	NOT_ENDS_WITH: 'does not end with',
	IN: 'is any of',
	NOT_IN: 'is none of',
	GT: 'is greater than',
	GTE: 'is at least',
	LT: 'is less than',
	LTE: 'is at most',
	BETWEEN: 'is between',
	IS_BLANK: 'is blank',
	IS_NOT_BLANK: 'is not blank',
};

/** `IN`/`NOT_IN`/`BETWEEN` carry more than one value. Usually that already
 *  arrives as a real array; parsed as JSON only as a defensive fallback for
 *  a value some other writer stored as a JSON-encoded string, so a reader
 *  sees values either way, never braces. */
function listValues(raw: string | string[]): string[] {
	if (Array.isArray(raw)) return raw;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed)) return parsed.map(String);
	} catch {
		// Not JSON — fall through to treating it as one value.
	}
	return [raw];
}

/** "document type is ZTNR" — the operator as a word, for a reader who never
 *  typed `STARTS_WITH`. Falls back to the server's own sentence when the
 *  rule carries a composed condition, which this cannot phrase. */
function sentence(rule: Rule): string {
	if (rule.conditionDescription) return rule.conditionDescription;
	const field = rule.matchField.replace(/_/g, ' ');
	const op = rule.matchOperator;
	const verb = OPERATOR_LABELS[op] ?? op;

	if (BLANK_OPERATORS.has(op)) return `${field} ${verb}`;

	if (op === 'IN' || op === 'NOT_IN') {
		const values = listValues(rule.matchValue)
			.map((value) => `"${value}"`)
			.join(', ');
		return `${field} ${verb} ${values}`;
	}

	if (op === 'BETWEEN') {
		const [low, high] = listValues(rule.matchValue);
		return `${field} ${verb} "${low ?? ''}" and "${high ?? ''}"`;
	}

	// Every other operator takes one plain value. `matchValue` is only ever
	// an array for the two families handled above, so this is a string here.
	return `${field} ${verb} "${Array.isArray(rule.matchValue) ? (rule.matchValue[0] ?? '') : rule.matchValue}"`;
}

/** Whether a rule's condition is an `all`/`any` group rather than one leaf.
 *
 *  `presentRule` (`firmService.ts`) always sends a `condition` tree now —
 *  a single leaf for a flat rule, never `null` — so a rule's presence alone
 *  says nothing about whether it's composed; only its SHAPE does. Both
 *  checks in `deadReason` below only make sense for a single leaf: the flat
 *  `matchField`/`matchOperator`/`matchValue` triple is the whole rule for
 *  those (`ruleCondition.flatMatchValue` keeps it a faithful stand-in even
 *  for `IN`/`BETWEEN`), but for a group it is only the tree's FIRST leaf —
 *  comparing just that would flag a rule as dead, or as a duplicate, for
 *  reasons that have nothing to do with what it actually matches. */
function isComposed(condition: unknown): boolean {
	return (
		typeof condition === 'object' &&
		condition !== null &&
		('all' in condition || 'any' in condition)
	);
}

/** Why this rule will never fire, or null if it will.
 *
 *  `earlier` is every rule ahead of it in pass order. Shadowing is only
 *  claimed for an exact match on the same field, operator and value — a
 *  CONTAINS rule can overlap an EQUALS one without strictly shadowing it,
 *  and a warning that cries wolf is worse than none.
 *
 *  `matchableFields` is null until `GET /reference/canonical-fields` has
 *  answered — the field-matchability half of the check is skipped rather
 *  than claiming every field is unmatchable during that first render. */
function deadReason(
	rule: Rule,
	earlier: Rule[],
	matchableFields: Set<string> | null,
): string | null {
	if (
		!isComposed(rule.condition) &&
		matchableFields &&
		!matchableFields.has(rule.matchField)
	) {
		return `“${rule.matchField.replace(/_/g, ' ')}” is not a column this firm's schema defines — it is skipped, so this rule never classifies anything.`;
	}
	const shadow = earlier.find(
		(other) =>
			!isComposed(other.condition) &&
			!isComposed(rule.condition) &&
			other.matchField === rule.matchField &&
			other.matchOperator === rule.matchOperator &&
			other.matchValue === rule.matchValue &&
			(other.fkClientId === rule.fkClientId || other.fkClientId === null),
	);
	if (shadow) {
		return `Priority ${shadow.priority} already claims exactly this, as ${shadow.classification}. First match wins, so this rule can never fire.`;
	}
	return null;
}

export default function RuleList({ onChanged }: { onChanged?: () => void }) {
	const { module } = useModule();
	const packs = useRulesetsQuery(module);
	/* The typed, server-owned field list — driving both the matchability check
     above and the field/type choices in the edit form below, instead of a
     second hand-kept copy of either. */
	const fields = useCanonicalFieldsQuery();
	const [confirmPublish, setConfirmPublish] = useState<Ruleset | null>(null);
	const [confirmDelete, setConfirmDelete] = useState<Rule | null>(null);
	const [editing, setEditing] = useState<{
		rule: Rule;
		pack: Ruleset;
	} | null>(null);

	const refresh = () => {
		void packs.refetch();
		onChanged?.();
	};

	const toast = useToast();
	const publishMutation = usePublishRulesetMutation();
	const removeMutation = useDeleteRuleMutation();

	const publish = {
		isPending: publishMutation.isPending,
		error: publishMutation.error,
		run: (rulesetId: string) =>
			publishMutation.mutate(rulesetId, {
				onSuccess: () => {
					setConfirmPublish(null);
					refresh();
					toast(
						'ok',
						'Pack published. Files processed from now on use these rules.',
					);
				},
			}),
	};

	const remove = {
		isPending: removeMutation.isPending,
		error: removeMutation.error,
		run: (ruleId: string) =>
			removeMutation.mutate(ruleId, {
				onSuccess: () => {
					setConfirmDelete(null);
					refresh();
					toast('ok', 'Rule deleted.');
				},
			}),
	};

	const openEditor = (rule: Rule, pack: Ruleset) => {
		setEditing({ rule, pack });
	};

	if (packs.isPending) return <Card title="Rule sets">Loading…</Card>;
	if (packs.error) {
		return (
			<Card title="Rule sets">
				<ErrorNotice error={packs.error} onRetry={packs.refetch} />
			</Card>
		);
	}

	const list = packs.data ?? [];
	const matchableFields = fields.data
		? new Set(fields.data.fields.map((item) => item.field))
		: null;

	return (
		<div className="stack">
			<ErrorNotice error={publish.error} />
			<ErrorNotice error={remove.error} />

			{list.length === 0 && (
				<Note
					tone="info"
					title="No classification rules written yet"
					row
				>
					Every row of every file stays unclassified until a rule set
					is published.
				</Note>
			)}

			{list.map((pack) => {
				const draft = pack.status === 'DRAFT';
				return (
					<Card
						key={pack.pkRulesetId}
						title={
							<span
								className="row"
								style={{
									gap: 'var(--s2)',
									alignItems: 'center',
								}}
							>
								<span>Rule set version {pack.version}</span>
								<StatusBadge
									group="rulesetStatus"
									value={pack.status}
								/>
							</span>
						}
						hint={
							pack.status === 'ACTIVE'
								? 'These are the rules classifying rows today.'
								: draft
									? 'Draft. Nothing uses these rules until the set is published.'
									: 'Superseded — kept so past reports stay reproducible.'
						}
						actions={
							draft && pack.rules.length > 0 ? (
								<Button
									variant="contained"
									size="small"

									disabled={publish.isPending}
									onClick={() => setConfirmPublish(pack)}
								>
									Publish rule set
								</Button>
							) : undefined
						}
					>
						{pack.rules.length === 0 ? (
							<p className="small mut">
								No rules in this set yet.
							</p>
						) : (
							<div className="stack2">
								{pack.rules.map((rule, index) => {
									const dead = deadReason(
										rule,
										pack.rules.slice(0, index),
										matchableFields,
									);
									return (
										<div
											key={rule.pkClassificationRuleId}
											className="row spread up-item"
										>
											<span className="stack2">
												<span className="b">
													{sentence(rule)}{' '}
													<span className="mut">
														→
													</span>{' '}
													<StatusBadge
														group="classification"
														value={
															rule.classification
														}
														plain
													/>
												</span>
												<span className="l2 mut">
													priority {rule.priority} ·{' '}
													{rule.fkClientId
														? 'this client only'
														: 'every client'}
												</span>
												{dead && (
													<Note
														tone="warn"
														title="This rule will never fire"
														row
													>
														{dead}
													</Note>
												)}
											</span>
											<span
												className="row"
												style={{ gap: 'var(--s2)' }}
											>
												<Button
													variant="outlined"
													size="small"

													onClick={() =>
														openEditor(rule, pack)
													}
												>
													Edit
												</Button>
												{draft && (
													<Button
														variant="contained"
														color="error"
														size="small"

														disabled={
															remove.isPending
														}
														onClick={() =>
															setConfirmDelete(
																rule,
															)
														}
													>
														Delete
													</Button>
												)}
											</span>
										</div>
									);
								})}
							</div>
						)}
					</Card>
				);
			})}

			<ConfirmDialog
				open={confirmPublish !== null}
				title="Publish this rule set?"
				question={
					confirmPublish
						? `${confirmPublish.process} · v${confirmPublish.version}, ${confirmPublish.rules.length} rule${confirmPublish.rules.length === 1 ? '' : 's'}.`
						: ''
				}
				/* Both halves said plainly. People expect publishing to change the
           numbers they are already looking at, and it does not — files keep
           the classification they were processed with until somebody
           re-applies, which is the property that makes an issued report
           stable. */
				impact="Every file processed from now on will use these rules. Files already processed keep the classification they have until somebody re-applies the rules to them from the file page."
				confirmLabel="Publish"
				pending={publish.isPending}
				error={publish.error}
				onClose={() => setConfirmPublish(null)}
				onConfirm={() =>
					confirmPublish && publish.run(confirmPublish.pkRulesetId)
				}
			/>

			<ConfirmDialog
				open={confirmDelete !== null}
				title="Delete this rule?"
				question={confirmDelete ? sentence(confirmDelete) : ''}
				impact="A rule some file was already classified with cannot be deleted — the record of what ran has to survive — and the API will say so."
				confirmLabel="Delete"
				danger
				pending={remove.isPending}
				error={remove.error}
				onClose={() => setConfirmDelete(null)}
				onConfirm={() =>
					confirmDelete &&
					remove.run(confirmDelete.pkClassificationRuleId)
				}
			/>

			<Dialog
				open={editing !== null}
				onClose={() => setEditing(null)}
				title="Edit rule"
				subtitle={
					editing
						? `Rule set version ${editing.pack.version}`
						: undefined
				}
				/* A published pack's drawer needs only a Close button — see the body
           below. A draft's Save/Cancel pair lives inside `RuleBuilder`
           itself (rendered bare, no footer here) so this file does not have
           to mirror its pending/can-save state to drive a second copy of
           those buttons. */
				footer={
					editing && editing.pack.status !== 'DRAFT' ? (
						<div className="spread">
							<span />
							<Button
								variant="outlined"
								onClick={() => setEditing(null)}
							>
								Close
							</Button>
						</div>
					) : undefined
				}
			>
				{editing && editing.pack.status !== 'DRAFT' ? (
					/* The user chose this explicitly: no auto-clone into a draft, and
             no request that would just come back a 400. One honest sentence
             about why nothing happens instead. */
					<Note tone="info" title="Published rules cannot be changed">
						{sentence(editing.rule)} was published as part of Rule
						set version {editing.pack.version}, which is{' '}
						{editing.pack.status === 'ACTIVE'
							? 'live'
							: 'superseded'}{' '}
						— a published rule is what made a past report's
						classification reproducible. Start a draft rule set to
						change how rows are classified from here on.
					</Note>
				) : editing ? (
					<RuleBuilder
						rule={{
							id: editing.rule.pkClassificationRuleId,
							clientId: editing.rule.fkClientId,
							priority: editing.rule.priority,
							classification: editing.rule
								.classification as Classification,
							condition: editing.rule.condition,
						}}
						onSaved={() => {
							setEditing(null);
							refresh();
						}}
						onCancel={() => setEditing(null)}
					/>
				) : null}
			</Dialog>
		</div>
	);
}
